import React, { useRef, useEffect } from 'react';
import {
  Box,
  Popper,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
  ClickAwayListener,
  ListSubheader,
} from '@mui/material';
import { MacroSuggestion } from '../types';
import styles from './MacroSuggestions.module.scss';
import { generateSampleURL } from '../utils';
import { useMacroEditorConfig } from '../MacroEditorContext';

interface MacroSuggestionsProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  suggestions: MacroSuggestion[];
  selectedIndex: number;
  onSelect: (macro: MacroSuggestion) => void;
  onClose: () => void;
  urlTemplate: string;
  /** optional footer rendered below the suggestion list (e.g. macro settings) */
  renderFooter?: () => React.ReactNode;
}

interface GroupedSuggestion {
  key: string;
  displayName: string;
  items: MacroSuggestion[];
}

export const MacroSuggestions: React.FC<MacroSuggestionsProps> = ({
  open,
  anchorEl,
  suggestions = [],
  selectedIndex,
  onSelect,
  onClose,
  urlTemplate,
  renderFooter,
}) => {
  const listItemRefs = useRef<Map<number, HTMLElement>>(new Map());
  const listRef = useRef<HTMLUListElement>(null);

  const { parameterizedURLs, parentDatasetSamples, dateFormat, dateTimeUnit } =
    useMacroEditorConfig();

  // Generate sample URL with all macros replaced
  const sampleURL = React.useMemo(() => {
    return generateSampleURL(
      urlTemplate,
      parameterizedURLs,
      parentDatasetSamples,
      dateFormat,
      dateTimeUnit,
    );
  }, [urlTemplate, parameterizedURLs, parentDatasetSamples, dateFormat, dateTimeUnit]);

  // Group suggestions by type
  const groupedSuggestions = React.useMemo(() => {
    if (!suggestions) return [];

    const groups: {
      key: string;
      displayName: string;
      items: MacroSuggestion[];
    }[] = [];
    const indexByType = new Map<string, number>();

    for (const suggestion of suggestions) {
      const typeKey = suggestion.type || 'other';
      const displayName = suggestion.type_display || 'Other';
      const idx = indexByType.get(typeKey);

      if (idx === undefined) {
        indexByType.set(typeKey, groups.length);
        groups.push({ key: typeKey, displayName, items: [suggestion] });
      } else {
        groups[idx].items.push(suggestion);
      }
    }

    return groups;
  }, [suggestions]);

  const groupStartIndices = React.useMemo(() => {
    const indices: number[] = [];
    let currentIndex = 0;

    groupedSuggestions.forEach((group) => {
      indices.push(currentIndex);
      currentIndex += group.items.length;
    });

    return indices;
  }, [groupedSuggestions]);

  useEffect(() => {
    if (selectedIndex >= 0 && open) {
      const selectedItem = listItemRefs.current.get(selectedIndex);
      if (selectedItem) {
        selectedItem.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'nearest',
        });
      }
    }
  }, [selectedIndex, open]);

  return (
    <Popper
      open={open && suggestions && suggestions.length > 0}
      anchorEl={anchorEl}
      placement="bottom-start"
      className={styles.popper}
      modifiers={[
        {
          name: 'offset',
          options: {
            offset: [0, 4],
          },
        },
        {
          // Custom modifier to make popper width match the anchor element (MacroEditor) width
          name: 'sameWidth',
          enabled: true,
          fn: ({ state }) => {
            state.styles.popper.width = `${state.rects.reference.width}px`;
          },
          phase: 'beforeWrite',
          requires: ['computeStyles'],
        },
      ]}
    >
      <ClickAwayListener onClickAway={onClose} mouseEvent="onMouseDown">
        <Paper elevation={8} className={styles.paper}>
          {urlTemplate && /\{[^}]+\}/.test(urlTemplate) && (
            <Box className={styles.resultHeader}>
              <Typography
                variant="caption"
                sx={{ display: 'block', marginBottom: 'var(--spacing-sm)' }}
              >
                Sample Result:
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontFamily: 'monospace',
                  fontSize: 'var(--font-size-base-sm)',
                  wordBreak: 'break-all',
                }}
              >
                {sampleURL}
              </Typography>
            </Box>
          )}
          <Box className={styles.header}>
            <Stack direction="row" spacing={2}>
              <Typography variant="caption" className={styles.headerText}>
                ↑↓ Navigate
              </Typography>
              <Typography variant="caption" className={styles.headerText}>
                ↵ Select
              </Typography>
              <Typography variant="caption" className={styles.headerText}>
                <code>esc</code> Close
              </Typography>
            </Stack>
          </Box>
          <List ref={listRef} className={styles.list}>
            {groupedSuggestions.map((group: GroupedSuggestion, groupIdx: number) => {
              const currentIndex = groupStartIndices[groupIdx];
              return (
                <Box key={group.key}>
                  <ListSubheader className={styles.groupHeader}>{group.displayName}</ListSubheader>
                  {group.items.map((suggestion: MacroSuggestion, itemIdx: number) => {
                    const absoluteIndex = currentIndex + itemIdx;
                    return (
                      <ListItem key={suggestion.name} disablePadding dense>
                        <ListItemButton
                          ref={(el) => {
                            if (el) {
                              listItemRefs.current.set(absoluteIndex, el);
                            } else {
                              listItemRefs.current.delete(absoluteIndex);
                            }
                          }}
                          selected={absoluteIndex === selectedIndex}
                          onClick={() => onSelect(suggestion)}
                          className={styles.listItem}
                        >
                          <ListItemText
                            primary={
                              <Box className={styles.suggestionContent}>
                                <Typography
                                  variant="body1"
                                  className={styles.suggestionDisplayName}
                                >
                                  {suggestion.display_name}
                                </Typography>
                                <Typography variant="body2" className={styles.suggestionName}>
                                  {suggestion.name}
                                </Typography>
                              </Box>
                            }
                          />
                        </ListItemButton>
                      </ListItem>
                    );
                  })}
                </Box>
              );
            })}
          </List>
          {renderFooter && <Box className={styles.footer}>{renderFooter()}</Box>}
        </Paper>
      </ClickAwayListener>
    </Popper>
  );
};
